import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { DiscoveryRow, type DiscoveryRowModel } from './V2DiscoveryRow';
import { type RowState } from './V2FocusRow';
import { useFocusedList } from '../hooks/useFocusedList';
import {
  applies,
  isValidSubjectName,
  SUBJECT_NAME_MAX,
  type DiscoveryAnswers,
  type DiscoveryQuestion,
  type EscalationCheck,
  type DiscoveryRule,
} from '../api/v2TakeApi';

const GREEN = 'bg-[#026857] hover:bg-[#025247]';

/**
 * Discovery — the intake that opens an MVP 2 assessment (Feature 110, §4).
 *
 * None of this is scored. It establishes who the assessment is about and the
 * relevance and urgency signals the priority engine reads, which is why a
 * crisis answered here can reorder what the client is told to do without
 * moving their readiness score by a point.
 *
 * One question at a time, in the same focused list the pillars use (Feature
 * 119). This was a single scrollable page with every question expanded, and the
 * note here said so: "ten questions plus conditionals is a long page on a phone;
 * upgrade path: group into steps". The focused list is that upgrade without the
 * grouping — it collapses the page rather than paginating it, so the gate rules
 * keep driving the reveal and no question had to be assigned to a step.
 *
 * The rhythm differs from the pillars in one place, and has to. A readiness
 * question is answered by its one tap, so the row can close itself. A
 * multi-select is answered when the client says it is, so those rows wait for
 * them — only D3 and D8 of the fourteen.
 */

type V2DiscoveryScreenProps = {
  questions: DiscoveryQuestion[];
  escalationChecks: EscalationCheck[];
  escalationShowWhen: DiscoveryRule[];
  answers: DiscoveryAnswers;
  onAnswer: (questionId: string, optionKeys: string[]) => void;
  onBack: () => void;
  onContinue: () => void;
  isSaving?: boolean;
  saveError?: boolean;
  /**
   * The supporter's typed first name for the person they answer about. Passed
   * only while D1 is a supporter answer, which is what shows the field.
   */
  subjectName?: string;
  onSubjectNameChange?: (name: string) => void;
};

const CHECK_OPTIONS = [
  { key: 'yes', label: 'Yes' },
  { key: 'no', label: 'No' },
];

export function V2DiscoveryScreen({
  questions,
  escalationChecks,
  escalationShowWhen,
  answers,
  onAnswer,
  onBack,
  onContinue,
  isSaving = false,
  saveError = false,
  subjectName,
  onSubjectNameChange,
}: V2DiscoveryScreenProps) {
  // Optional, so it is not a row and never counts toward "answered" — but a
  // name the server would refuse holds Continue rather than failing the save.
  const nameIsValid = subjectName === undefined || isValidSubjectName(subjectName);

  /**
   * The rows a given set of answers produces. A conditional appears as soon as
   * its trigger is answered and disappears when the trigger moves off it, so the
   * list is derived rather than fixed up front.
   *
   * Takes the answers rather than closing over them, because scheduling an
   * advance has to look at the list the answer is about to produce. Reading the
   * current one, an answer that changed a gate scheduled a position in a list
   * that no longer existed by the time it was used: changing a completed D4 to a
   * value that reveals the escalation checks found everything answered, scheduled
   * "nothing open", and then the checks appeared with no open row — and an
   * unanswered row is deliberately not clickable, so Continue was disabled with
   * no way left to satisfy it.
   */
  const rowsFor = (given: DiscoveryAnswers): DiscoveryRowModel[] => [
    ...questions
      .filter((question) => applies(given, question.showWhen))
      .map((question) => ({
        id: question.id,
        text: question.text,
        options: question.options,
        select: question.select,
        maxSelections: question.maxSelections,
      })),
    ...(applies(given, escalationShowWhen)
      ? escalationChecks.map((check, i) => ({
          id: check.id,
          text: check.text,
          options: CHECK_OPTIONS,
          select: 'SINGLE' as const,
          startsChecks: i === 0,
        }))
      : []),
  ];

  const rows = rowsFor(answers);
  const chosen = (id: string) => answers[id] ?? [];
  const answeredCount = rows.filter((row) => chosen(row.id).length > 0).length;
  // Every question on screen must be answered, conditionals included — they are
  // only on screen because their gate holds. Requiring just the ungated ones let
  // a revealed follow-up go blank, and the server then refused the completion
  // for a question the client was never told it had missed.
  const allAnswered = answeredCount === rows.length;

  const firstUnanswered = (given: DiscoveryAnswers): number | null => {
    const at = rowsFor(given).findIndex((row) => (given[row.id] ?? []).length === 0);
    return at === -1 ? null : at;
  };

  const { activeIndex, rowRef, continueRef, reopen, advanceTo } = useFocusedList({
    firstUnanswered: () => firstUnanswered(answers),
  });

  const pick = (position: number, row: DiscoveryRowModel, keys: string[]) => {
    onAnswer(row.id, keys);

    // A single-select is finished by the tap that made it. A multi-select is
    // finished when the client says so, through the row's own way out.
    if (row.select === 'SINGLE') {
      advanceTo(position, firstUnanswered({ ...answers, [row.id]: keys }));
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl">
      <p className="text-center text-xs font-semibold uppercase tracking-wide text-[#026857]">
        First, your situation
      </p>
      <h2 className="mt-1 text-center font-serif text-2xl text-gray-900">
        A few questions before we begin
      </h2>
      <p className="mx-auto mt-2 max-w-lg text-center text-sm leading-relaxed text-gray-500">
        These do not affect your readiness score. They help us understand what matters most for you
        right now.
      </p>

      <div className="mt-8 space-y-2">
        {rows.map((row, i) => {
          const open = i === activeIndex;
          const current = chosen(row.id);
          const state: RowState = open ? 'open' : current.length > 0 ? 'answered' : 'todo';
          const next = firstUnanswered(answers);

          return (
            <div key={row.id}>
              {/*
                The checks keep their framing without keeping their container: a
                card around them inside a list of cards is a nesting the layout
                does not need, and the heading carries the meaning on its own.
              */}
              {row.startsChecks && (
                <div className="mb-3 mt-6 border-l-2 border-[#f3c66c] pl-3">
                  <p className="text-[15px] font-medium text-gray-900">
                    A few quick checks about right now
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-gray-500">
                    Your answers here can move something to the top of your plan. They never change
                    your readiness score.
                  </p>
                </div>
              )}

              <DiscoveryRow
                row={row}
                position={i + 1}
                total={rows.length}
                state={state}
                chosen={current}
                isSaving={isSaving}
                doneLabel={next === null || next === i ? 'Done' : 'Next question'}
                onPick={(keys) => pick(i, row, keys)}
                onReopen={() => reopen(i)}
                onDone={() => advanceTo(i, firstUnanswered(answers))}
                rowRef={open ? rowRef : undefined}
              />

              {row.id === 'D1' && subjectName !== undefined && (
                <div className="mt-2 rounded-xl border border-gray-200 bg-white px-4 py-3">
                  <label htmlFor="subject-first-name" className="text-sm text-gray-700">
                    What&apos;s their first name? (optional)
                  </label>
                  <Input
                    id="subject-first-name"
                    className="mt-1"
                    autoComplete="off"
                    maxLength={SUBJECT_NAME_MAX}
                    value={subjectName}
                    aria-invalid={!nameIsValid}
                    aria-describedby={nameIsValid ? undefined : 'subject-first-name-error'}
                    onChange={(event) => onSubjectNameChange?.(event.target.value)}
                  />
                  {!nameIsValid && (
                    <p
                      id="subject-first-name-error"
                      role="alert"
                      className="mt-1 text-xs text-red-600"
                    >
                      Use letters, spaces, hyphens, apostrophes or periods.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <p className="text-[13px] text-gray-400">
          {answeredCount} of {rows.length} answered
        </p>
        <div className="flex items-center gap-2">
          {/* Disabled while the step is in flight: the save advances on success,
              so going back mid-request lands the client on the next screen
              anyway — their navigation silently loses to a response. */}
          <Button type="button" variant="ghost" disabled={isSaving} onClick={onBack}>
            Back
          </Button>
          <Button
            type="button"
            className={GREEN}
            disabled={!allAnswered || !nameIsValid || isSaving}
            onClick={onContinue}
            ref={continueRef}
          >
            {isSaving ? 'Saving…' : 'Continue'}
          </Button>
        </div>
      </div>
      {saveError && (
        <p className="mt-2 text-center text-sm text-red-600">
          Couldn&apos;t save your answers. Please try again.
        </p>
      )}
    </div>
  );
}
